import React, { useState } from "react"
import { useToggle } from "../../hooks/useToggle"
import Animate, { AnimationTypes } from "../Animate/Animate"
import { useTranslation } from "react-i18next"
import { DayOfWeekLabels, EDayOfWeek } from "../../Enum/EDayOfWeek"
import InputField from "../InputField/InputField"
import { faClock } from "@fortawesome/free-solid-svg-icons"

const daysOfWeek = [
  EDayOfWeek.Monday,
  EDayOfWeek.Tuesday,
  EDayOfWeek.Wednesday,
  EDayOfWeek.Thursday,
  EDayOfWeek.Friday,
  EDayOfWeek.Saturday,
  EDayOfWeek.Sunday,
]

interface DateTimeSelectorProps {
  initialDayWeek?: EDayOfWeek[]
  initialTimeStart?: string
  initialTimeFinish?: string
  disable: boolean
  onDaysChange: (days: EDayOfWeek[]) => void
  onTimeStartChange: (time: string) => void
  onTimeEndChange: (time: string) => void
}

export const DateTimeSelector: React.FC<DateTimeSelectorProps> = ({
  onDaysChange,
  onTimeStartChange,
  onTimeEndChange,
  initialDayWeek,
  initialTimeStart,
  initialTimeFinish,
  disable,
}) => {
  const { t } = useTranslation("dateTimeSelector")

  const [selectedTimeStart, setSelectedTimeStart] = useState<string>(initialTimeStart ?? "")

  const [selectedTimeEnd, setSelectedTimeEnd] = useState<string>(initialTimeFinish ?? "")

  const { selectedType, handleToggleSelection } = useToggle(initialDayWeek ?? daysOfWeek)

  return (
    <Animate type={AnimationTypes.fadeInTraslate} duration="1s">
      <div className="w-full bg-white rounded-2xl shadow-md p-4 space-y-5">
        <div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {daysOfWeek.map((day) => (
              <label
                key={day}
                className={`
              flex items-center gap-2 px-3 py-2 rounded-lg border-2
              cursor-pointer transition
              ${selectedType.includes(day) ? "bg-orange-50 border-orange-400" : "bg-gray-50 border-gray-200"}
              ${disable ? "opacity-50 cursor-not-allowed" : "hover:bg-orange-100"}
            `}
              >
                <input
                  type="checkbox"
                  disabled={disable}
                  checked={selectedType.includes(day)}
                  onChange={() => handleToggleSelection(false, day, onDaysChange)}
                  className="accent-orange-500"
                  data-interaction-description-key="interactionDescriptions:components.dateTimeSelector.dateTimeSelector.input.input"
                />

                <span className="text-sm text-gray-700">{t(`days.${DayOfWeekLabels[day]}`)}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="border-t border-gray-200"></div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">{t("label_begin_time")}</label>

            <InputField
              type="time"
              placeholder={t("label_person_percentage_place_holder")}
              onInvalidMessage={t("label_person_percentage_onInvalidMessage")}
              icon={faClock}
              value={selectedTimeStart}
              onChange={(e) => {
                setSelectedTimeStart(e.target.value)
                onTimeStartChange(e.target.value)
              }}
              disable={disable}
              descriptionKey="interactionDescriptions:components.dateTimeSelector.dateTimeSelector.input.personPercentagePlaceHolder"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">{t("label_end_time")}</label>

            <InputField
              type="time"
              placeholder={t("label_person_percentage_place_holder")}
              onInvalidMessage={t("label_person_percentage_onInvalidMessage")}
              icon={faClock}
              value={selectedTimeEnd}
              onChange={(e) => {
                setSelectedTimeEnd(e.target.value)
                onTimeEndChange(e.target.value)
              }}
              disable={disable}
              descriptionKey="interactionDescriptions:components.dateTimeSelector.dateTimeSelector.input.personPercentagePlaceHolder2"
            />
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              disabled={disable}
              onClick={() => {
                setSelectedTimeStart("")
                setSelectedTimeEnd("")
                onTimeStartChange("")
                onTimeEndChange("")
              }}
              className="
            px-4 py-2 text-sm font-semibold rounded-lg
            ring-2 ring-red-500
            text-gray-700
            hover:bg-red-100
            transition
            disabled:opacity-50
          "
              data-interaction-description-key="interactionDescriptions:components.dateTimeSelector.dateTimeSelector.button.clearTime"
            >
              {t("buttons:button_clear_time")}
            </button>
          </div>
        </div>
      </div>
    </Animate>
  )
}
